import { Action, Dispatch } from 'redux';
import toArray from '@stripcash/ui-kit/utils/toArray';
import {
  ActionCreatorWithPayload,
  ActionCreator,
  PayloadAction,
} from '@reduxjs/toolkit';

import { terminate } from './createApp';

export enum Priority {
  Immediate,
  High,
  Medium,
  Low,
  NeverMind,
}

export interface BatchDispatch {
  <A extends Action = Action>(action: A): void;
  <A extends Action = Action>(actions: A[]): void;
}
export type MiddlewareAPI<S = unknown, D = unknown, G = unknown> = {
  getState: () => S;
  dispatch: BatchDispatch;
  broadcast: BatchDispatch;
  getGlobalState: () => G;
  deps: D;
};
export interface Middleware<S = any, D = any> {
  (
    api: MiddlewareAPI<S, D>,
  ): (next: Dispatch<PayloadAction>) => (action: PayloadAction) => any;
  priority?: Priority;
}

type AnyActionCreatorWithPayload =
  | ActionCreatorWithPayload<any>
  | ActionCreatorWithPayload<any>[];

type HandlerPayload<A> =
  A extends ActionCreator<{ payload: infer P }>
    ? P
    : A extends ActionCreator<{ payload: infer P }>[]
      ? P
      : never;

const checkTypeBy =
  (actionsReactOn: AnyActionCreatorWithPayload) =>
  ({ type }) =>
    toArray(actionsReactOn).some((action) => action.type === type);

const createMiddleware = <
  A extends AnyActionCreatorWithPayload,
  S extends {} = {},
  D = {},
>(
  actionsReactOn: A,
  handler: (payload: HandlerPayload<A>, store: MiddlewareAPI<S, D>) => void,
  { priority = Priority.Medium }: { priority?: Priority } = {},
): Middleware<S, D> => {
  let terminated = false;
  const middleware: Middleware<S, D> = (store) => (next) => (action) => {
    const result = next(action);

    if (!terminated) {
      const resultArr = toArray(result);

      if (resultArr.some(checkTypeBy(terminate))) {
        terminated = true;
      }
      resultArr
        .filter(checkTypeBy(actionsReactOn))
        .forEach(({ payload }) => handler(payload as HandlerPayload<A>, store));
    }

    return result;
  };

  middleware.priority = priority;

  return middleware;
};

export default createMiddleware;
